import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { CourseFilterParams } from "../../components/Dashboard/services/coursesFilterService";

export interface Course {
	id: string;
	uuid: string;
	title: string;
	description: string;
	institute: string;
	duration: string;
	level: string | null;
	lastDate: string;
	admissionStartDate?: string;
	admissionEndDate?: string;
	classStartDate?: string;
	meritCriteria?: string | string[] | null;
	fundingType?: string | string[] | null;
	fundedBy?: string | null;
	isApplied?: boolean;
	shift?: {
		id: number;
		uuid: string;
		name: string;
	};
	assessmentBody?: {
		id: number;
		name: string;
		description: string;
	} | null;
	feeMode?: {
		id: number;
		name: string;
	} | null;
	isRegular?: boolean | null;
}

export interface Institute {
	id: string;
	uuid: string;
	name: string;
	status: string;
	logo?: string | null;
	location: {
		address: string;
		province: string;
		district: string;
		city: string;
	};
	coursesCount: number;
	batches: Course[]; // All batches/courses for this institute
}

export interface DashboardCoursesState {
	courses: Course[];
	institutes: Institute[]; // New: Store institutes instead of courses
	filteredCourses: Course[]; // Store filtered courses from preferences
	filteredInstitutes: Institute[]; // New: Store filtered institutes
	hasFilteredResults: boolean; // Flag to track if preferences were applied
	isPreference: boolean; // Flag to track if preferences are set
	isLoading: boolean;
	isError: boolean;
	error: string | null;
	page: number;
	hasNextPage: boolean;
	isFetching: boolean;
	// New properties for filtered courses pagination
	filteredPage: number;
	filteredLimit: number;
	filteredHasNextPage: boolean;
	filteredIsFetching: boolean;
	filteredTotalItems: number;
	// Store filter parameters for persistence
	storedFilterParams: Omit<CourseFilterParams, "page" | "limit">;
}

const initialState: DashboardCoursesState = {
	courses: [],
	institutes: [],
	filteredCourses: [],
	filteredInstitutes: [],
	hasFilteredResults: false,
	isPreference: false,
	isLoading: false,
	isError: false,
	error: null,
	page: 1,
	hasNextPage: true,
	isFetching: false,
	// New initial state for filtered courses pagination
	filteredPage: 1,
	filteredLimit: 10,
	filteredHasNextPage: true,
	filteredIsFetching: false,
	filteredTotalItems: 0,
	// Initial state for stored filter parameters
	storedFilterParams: {} as Omit<CourseFilterParams, "page" | "limit">,
};

const dashboardCoursesSlice = createSlice({
	name: "dashboardCourses",
	initialState,
	reducers: {
		setCoursesLoading: (state, action: PayloadAction<boolean>) => {
			state.isLoading = action.payload;
		},
		setCoursesError: (state, action: PayloadAction<string>) => {
			state.isError = true;
			state.error = action.payload;
			state.isLoading = false;
			state.isFetching = false;
		},
		clearCoursesError: state => {
			state.isError = false;
			state.error = null;
		},
		setCourses: (state, action: PayloadAction<{ courses: Course[]; page: number; hasNextPage: boolean }>) => {
			state.courses = action.payload.courses;
			state.page = action.payload.page;
			state.hasNextPage = action.payload.hasNextPage;
			state.isLoading = false;
			state.isError = false;
			state.error = null;
		},
		addMoreCourses: (state, action: PayloadAction<{ courses: Course[]; page: number; hasNextPage: boolean }>) => {
			state.courses = [...state.courses, ...action.payload.courses];
			state.page = action.payload.page;
			state.hasNextPage = action.payload.hasNextPage;
			state.isFetching = false;
		},
		setFetching: (state, action: PayloadAction<boolean>) => {
			state.isFetching = action.payload;
		},
		setFilteredCourses: (state, action: PayloadAction<Course[]>) => {
			state.filteredCourses = action.payload;
			state.hasFilteredResults = true;
			state.isPreference = true;
			// Reset pagination for new filtered results - keep limit constant at 10
			state.filteredPage = 1;
			state.filteredLimit = 10;
			state.filteredHasNextPage = true;
			state.filteredIsFetching = false;
		},
		addMoreFilteredCourses: (
			state,
			action: PayloadAction<{
				courses: Course[];
				page: number;
				limit: number;
				hasNextPage: boolean;
				totalItems: number;
			}>,
		) => {
			state.filteredCourses = [...state.filteredCourses, ...action.payload.courses];
			state.filteredPage = action.payload.page;
			state.filteredLimit = 10; // Always keep limit constant at 10
			state.filteredHasNextPage = action.payload.hasNextPage;
			state.filteredTotalItems = action.payload.totalItems;
			state.filteredIsFetching = false;
		},
		setFilteredFetching: (state, action: PayloadAction<boolean>) => {
			state.filteredIsFetching = action.payload;
		},
		setPreferencesFlag: (state, action: PayloadAction<boolean>) => {
			state.isPreference = action.payload;
		},
		clearFilteredCourses: state => {
			state.filteredCourses = [];
			state.hasFilteredResults = false;
		},
		setStoredFilterParams: (state, action: PayloadAction<Omit<CourseFilterParams, "page" | "limit">>) => {
			state.storedFilterParams = action.payload;
		},
		setInstitutes: (state, action: PayloadAction<Institute[]>) => {
			state.institutes = action.payload;
		},
		setFilteredInstitutes: (state, action: PayloadAction<Institute[]>) => {
			state.filteredInstitutes = action.payload;
			state.hasFilteredResults = true;
		},
		resetCourses: state => {
			state.courses = [];
			state.institutes = [];
			state.filteredCourses = [];
			state.filteredInstitutes = [];
			state.hasFilteredResults = false;
			state.isPreference = false;
			state.page = 1;
			state.hasNextPage = true;
			state.isLoading = false;
			state.isError = false;
			state.error = null;
			state.isFetching = false;
			// Reset filtered pagination properties - limit always stays at 10
			state.filteredPage = 1;
			state.filteredLimit = 10;
			state.filteredHasNextPage = true;
			state.filteredIsFetching = false;
			state.filteredTotalItems = 0;
			// Reset stored filter parameters
			state.storedFilterParams = {} as Omit<CourseFilterParams, "page" | "limit">;
		},
	},
});

export const {
	setCoursesLoading,
	setCoursesError,
	clearCoursesError,
	setCourses,
	addMoreCourses,
	setFetching,
	setFilteredCourses,
	addMoreFilteredCourses,
	setFilteredFetching,
	setPreferencesFlag,
	clearFilteredCourses,
	setStoredFilterParams,
	setInstitutes,
	setFilteredInstitutes,
	resetCourses,
} = dashboardCoursesSlice.actions;

export default dashboardCoursesSlice.reducer;
